import { cx } from '@emotion/css';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Button } from '@sortlist-frontend/design-system/legacy';
import { useIsVisible } from '@sortlist-frontend/utils';
import { MutableRefObject, TransitionEvent, useEffect, useRef, useState } from 'react';

import * as S from './styles';

const DIRECTION_TYPE = {
  next: 'NEXT',
  prev: 'PREV',
};

type SliderProps = {
  current: number;
  needTransition: boolean;
  isInTransition: boolean;
  direction?: string;
};

type Props<T extends { name: string }> = {
  items: T[];
  clonedBlocksCount: number;
  Card: React.ComponentType<T>;
  className?: string;
  cardMargin?: number;
  cardWidth?: number;
  autoPlay?: boolean;
  cardContainerStyle?: string;
};

export const Slider = <T extends { name: string }>(props: Props<T>) => {
  const {
    autoPlay = true,
    Card,
    items,
    cardContainerStyle,
    clonedBlocksCount,
    className,
    cardMargin = 4,
    cardWidth = 320,
  } = props;

  const defaultSliderOptions = {
    current: clonedBlocksCount,
    needTransition: true,
    direction: '',
    isInTransition: false,
  };
  const [sliderOptions, setSliderOptions] = useState<SliderProps>(defaultSliderOptions);
  const [intervalId, setIntervalId] = useState<ReturnType<typeof setInterval> | undefined>();
  const [visibleCardsCount, setVisibleCardsCount] = useState<number>(0);
  const [hasAllItemsInView, setHasAllItemsInView] = useState<boolean>(false);

  const cardsContainerRef = useRef<HTMLDivElement | null>(null);
  const inViewport = useIsVisible({ ref: cardsContainerRef as unknown as MutableRefObject<Element> });

  const cardTotalWidth = cardWidth + cardMargin * 2;

  const agenciesWidth = (items?.length || 1) * cardTotalWidth;

  const disableAction = (index: number) => {
    if (autoPlay) return false;

    const isLastCardInView = sliderOptions.current + visibleCardsCount === (items?.length ?? 0);
    const isFirstCardInview = sliderOptions.current === 0;

    return index === 0 ? isFirstCardInview : isLastCardInView;
  };

  useEffect(() => {
    if (autoPlay) return;
    const updateVisibleCardsCount = () => {
      if (cardsContainerRef.current == null) return;
      const containerWidth = cardsContainerRef.current.clientWidth;

      setHasAllItemsInView(containerWidth > agenciesWidth);
      setVisibleCardsCount(Math.floor(containerWidth / cardTotalWidth));

      setSliderOptions(defaultSliderOptions); //show first item if the screen is resized
    };

    updateVisibleCardsCount();

    window.addEventListener('resize', updateVisibleCardsCount);

    return () => {
      window.removeEventListener('resize', updateVisibleCardsCount);
    };
  }, []);

  const validateNextSlide = () => {
    if (sliderOptions.current === items?.length - clonedBlocksCount) {
      setSliderOptions({
        ...sliderOptions,
        needTransition: false,
        current: clonedBlocksCount,
        isInTransition: false,
      });
    } else {
      setSliderOptions({
        ...sliderOptions,
        isInTransition: false,
      });
    }
  };

  const validatePrevSlide = () => {
    if (sliderOptions.current === clonedBlocksCount - 1) {
      setSliderOptions({
        ...sliderOptions,
        needTransition: false,
        current: (items?.length || 0) - (clonedBlocksCount + 1),
        isInTransition: false,
      });
    } else {
      setSliderOptions({
        ...sliderOptions,
        isInTransition: false,
      });
    }
  };

  const handleNext = (manualTriggered = false) => {
    if (manualTriggered) clearInterval(intervalId as ReturnType<typeof setInterval>);
    if (sliderOptions.isInTransition) return;

    setSliderOptions((sliderOptions) => {
      return {
        ...sliderOptions,
        needTransition: true,
        current: sliderOptions.current + 1,
        direction: DIRECTION_TYPE.next,
        isInTransition: true,
      };
    });
  };

  const handlePrev = (manualTriggered = false) => {
    if (manualTriggered) clearInterval(intervalId as ReturnType<typeof setInterval>);
    if (sliderOptions.isInTransition) return;
    setSliderOptions({
      ...sliderOptions,
      needTransition: true,
      current: sliderOptions.current - 1,
      direction: DIRECTION_TYPE.prev,
      isInTransition: true,
    });
  };

  const transLateVal = () => {
    return -(sliderOptions.current * cardTotalWidth);
  };

  const sliderStyle = () => {
    if (sliderOptions.needTransition) {
      return {
        transform: `translateX(${transLateVal()}px)`,
        transition: 'transform 0.3s ease-in-out',
      };
    }

    return {
      transform: `translateX(${transLateVal()}px)`,
    };
  };

  const handleSliderTranslateEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName === 'transform') {
      switch (sliderOptions.direction) {
        case DIRECTION_TYPE.next:
          validateNextSlide();
          break;
        case DIRECTION_TYPE.prev:
          validatePrevSlide();
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    setSliderOptions({
      ...sliderOptions,
      current: clonedBlocksCount,
      needTransition: false,
    });
  }, [clonedBlocksCount]);

  useEffect(() => {
    if (inViewport && autoPlay) {
      const intervalId = setInterval(() => {
        handleNext();
      }, 3000);

      setIntervalId(intervalId);

      // clean up interval function
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [inViewport]);

  const handleOnClick = (index: number) => {
    if (index === 0) {
      handlePrev(true);
    } else {
      handleNext(true);
    }
  };

  return (
    <div className={cx(className)}>
      <div ref={cardsContainerRef} className="py-4 overflow-hidden">
        <S.CardsContainer
          isInContainer={!autoPlay}
          className={cx(cardContainerStyle, 'layout-row layout-align-start-stretch')}
          style={{ ...sliderStyle(), width: agenciesWidth }}
          onTransitionEnd={handleSliderTranslateEnd}>
          {items?.map((item, index) => {
            // eslint-disable-next-line react/no-array-index-key
            return <Card key={`${item.name}-${index}`} {...item} />;
          })}
        </S.CardsContainer>
      </div>
      {!hasAllItemsInView && (
        <div className="layout-row layout-align-center-center pt-8">
          {Array.from(Array(2), (e: number, index: number) => {
            const disabled = disableAction(index);
            return (
              <div className="p-2" key={index === 0 ? 'back-btn' : 'forward-btn'}>
                <Button
                  fab
                  buttonVariant="dark"
                  buttonStyle="primary"
                  size="lg"
                  onClick={() => handleOnClick(index)}
                  disabled={disabled}
                  icon={
                    index === 0 ? <ArrowBack style={{ fontSize: 24 }} /> : <ArrowForward style={{ fontSize: 24 }} />
                  }
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
